/**
 * Firestore documents in, domain objects out — field by field, both directions.
 *
 * This file imports no Firebase SDK, and must not start to. `platform/` is
 * compiled into the browser bundle *and* inlined into the Cloud Functions
 * bundle, and those two see different SDKs: `firebase/firestore` and
 * `firebase-admin/firestore` export incompatible `Timestamp` classes. So the
 * mapping is expressed over plain records, and each side wraps it in its own
 * converter — `app/src/api/converter.ts` and `functions/src/converter.ts`.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: a converter constructs its domain type
 * field by field and never spreads the raw document. `{ ...snap.data() }` puts
 * whatever the document happens to hold into the query cache — including a
 * `Timestamp`, which this app then persists to IndexedDB and reads back as a
 * bare `{seconds, nanoseconds}` with no `.toDate()` on it. The failure is not a
 * type error; it is a date that renders as "Invalid Date" for one user whose
 * cache predates a schema change, and nothing in review catches it.
 */
import type { z } from 'zod';

/** What a Firestore `Timestamp` looks like from the outside, in either SDK. */
type TimestampLike = { toMillis: () => number } | { seconds: number; nanoseconds: number };

const isTimestampLike = (v: unknown): v is TimestampLike =>
  typeof v === 'object' &&
  v !== null &&
  ('toMillis' in v || ('seconds' in v && 'nanoseconds' in v));

/**
 * Anything a Firestore field might hold for a moment in time, as milliseconds.
 * Handles the live SDK's `Timestamp`, the dehydrated `{seconds, nanoseconds}` a
 * persisted cache hands back, a `Date`, and a number already in milliseconds.
 *
 * Returns `null` for a field a pending `serverTimestamp()` has left unresolved —
 * which offline is every write until it syncs. Callers decide what to show;
 * nobody may assume it is a number.
 */
export const toMillis = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return value.getTime();
  if (isTimestampLike(value)) {
    if ('toMillis' in value) return value.toMillis();
    return value.seconds * 1000 + Math.round(value.nanoseconds / 1e6);
  }
  return null;
};

/** `toMillis`, for a field the schema says is required. */
export const requireMillis = (value: unknown, field: string): number => {
  const millis = toMillis(value);
  if (millis === null) throw new Error(`Expected a timestamp in "${field}", got ${String(value)}`);
  return millis;
};

/**
 * Parse a document into its domain type, naming the path when it fails.
 *
 * A zod error alone says "expected number, received undefined at recordedAt",
 * which is true of every measurement ever written. The path is the half that
 * makes it actionable.
 */
export const parseDoc = <T extends z.ZodType>(schema: T, path: string, data: unknown): z.infer<T> => {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new Error(`${path} does not match its schema: ${result.error.message}`, {
      cause: result.error,
    });
  }
  return result.data;
};

/**
 * Drop `undefined` before writing. Firestore rejects an `undefined` field value
 * outright unless `ignoreUndefinedProperties` is set, and setting that hides
 * genuine mistakes — a misspelled field silently not written. Being explicit
 * here means an optional field is written as `null`, which is a value the
 * schemas above actually describe.
 */
export const stripUndefined = <T extends Record<string, unknown>>(data: T): T =>
  Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined)) as T;
